import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { alpha, styled } from '@mui/material/styles';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import CloseIcon from '@mui/icons-material/Close';

import { formatAmount } from '@/lib/formatAmount';

import type { ConfirmPaySummary } from '@/pages/dokkaPay/pages/payment/components/PaymentFooter/components/ConfirmPayDialog/summary';

const DEFAULT_DATE_FORMAT = 'DD/MM/YYYY';
const EMPTY_VALUE = '—';

type ValueColor = 'primary.main';

const VendorBanner = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5, 2),
  marginBottom: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.primary.main, 0.14),
}));

const VendorLabel = styled(Typography)({
  display: 'block',
  textTransform: 'uppercase',
});

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  insetInlineEnd: theme.spacing(2),
  top: theme.spacing(1.5),
}));

interface SummaryRowProps {
  label: string;
  value: React.ReactNode;
  color?: ValueColor;
  icon?: React.ReactNode;
}

const SummaryRow: React.FC<SummaryRowProps> = ({ label, value, color, icon }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ py: 1.25 }}>
    <Typography variant="body1" color="text.secondary" sx={{ flexShrink: 0 }}>
      {label}
    </Typography>
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
      {icon}
      <Typography variant="body1" fontWeight={600} color={color} sx={{ textAlign: 'end', wordBreak: 'break-word' }}>
        {/* isolated so a minus sign or a list of numbers keeps its order in RTL */}
        <bdi>{value}</bdi>
      </Typography>
    </Stack>
  </Stack>
);

export interface ConfirmPayDialogViewProps {
  summary: ConfirmPaySummary;
  dateFormat?: string | null;
  onClose: () => void;
  /** Sends the payment. The dialog shows progress until it settles and stays open if it rejects. */
  onConfirm: () => Promise<unknown>;
}

const ConfirmPayDialogView: React.FC<ConfirmPayDialogViewProps> = ({ summary, dateFormat, onClose, onConfirm }) => {
  const { formatMessage } = useIntl();
  const [isPaying, setIsPaying] = useState(false);

  const money = (value: number) => formatAmount(value, summary.currencyCode);

  const handleConfirm = useCallback(() => {
    setIsPaying(true);
    // On success the owner closes (and unmounts) the dialog, so state is only reset
    // on failure; the error itself is already reported by the saga's toast.
    onConfirm().catch(() => setIsPaying(false));
  }, [onConfirm]);

  const handleClose = useCallback(() => {
    if (!isPaying) onClose();
  }, [isPaying, onClose]);

  const invoiceNumber = summary.isInvoiceListCollapsed
    ? formatMessage(
        { id: 'payment.confirmPay.invoicesCount', defaultMessage: '{count} invoices' },
        { count: summary.invoiceCount },
      )
    : summary.invoiceNumbers.join(', ') || EMPTY_VALUE;

  const paymentDate = summary.paymentDate ? moment(summary.paymentDate).format(dateFormat || DEFAULT_DATE_FORMAT) : '';

  const title = formatMessage({ id: 'payment.confirmPay.title', defaultMessage: 'Confirm & Pay' });

  return (
    <Dialog open onClose={handleClose} fullWidth maxWidth="sm" aria-labelledby="confirm-pay-dialog-title">
      <DialogTitle id="confirm-pay-dialog-title" sx={{ pr: 7 }}>
        {title}
        <CloseButton
          aria-label={formatMessage({ id: 'button.close', defaultMessage: 'Close' })}
          onClick={handleClose}
          disabled={isPaying}
        >
          <CloseIcon />
        </CloseButton>
      </DialogTitle>
      <DialogContent>
        <VendorBanner>
          <ReceiptOutlinedIcon color="primary" fontSize="medium" />
          <Box>
            <VendorLabel variant="subtitle2" color="text.secondary">
              {formatMessage({ id: 'payment.confirmPay.vendor', defaultMessage: 'Vendor' })}
            </VendorLabel>
            <Typography variant="subtitle2">{summary.vendorName}</Typography>
          </Box>
        </VendorBanner>

        <Stack divider={<Divider />} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <SummaryRow
            label={formatMessage({ id: 'payment.confirmPay.invoiceNumber', defaultMessage: 'Invoice #' })}
            value={invoiceNumber}
          />
          <SummaryRow
            label={formatMessage({ id: 'payment.confirmPay.currency', defaultMessage: 'Currency' })}
            value={summary.currencyCode || EMPTY_VALUE}
          />
          <SummaryRow
            label={formatMessage({ id: 'payment.confirmPay.invoiceAmount', defaultMessage: 'Invoice amount' })}
            value={money(summary.invoiceAmount)}
          />
          {summary.hasWithholdingTax && (
            <SummaryRow
              label={formatMessage(
                { id: 'payment.confirmPay.withholdingTax', defaultMessage: 'Withholding Tax ({percent}%)' },
                { percent: summary.withholdingTaxPercent },
              )}
              value={`- ${money(summary.withholdingTaxAmount)}`}
            />
          )}
          <SummaryRow
            label={formatMessage({ id: 'payment.confirmPay.paymentAmount', defaultMessage: 'Payment amount' })}
            value={money(summary.paymentAmount)}
            color="primary.main"
          />
          <SummaryRow
            label={formatMessage({ id: 'payment.confirmPay.payFrom', defaultMessage: 'Pay from' })}
            value={summary.payFromLabel ?? EMPTY_VALUE}
            icon={<AccountBalanceOutlinedIcon fontSize="small" color="action" />}
          />
          <SummaryRow
            label={formatMessage({ id: 'payment.confirmPay.payTo', defaultMessage: 'Pay to' })}
            value={summary.payToLabel ?? EMPTY_VALUE}
            icon={<AccountBalanceOutlinedIcon fontSize="small" color="action" />}
          />
          <SummaryRow
            label={formatMessage({ id: 'payment.confirmPay.payDate', defaultMessage: 'Pay date' })}
            value={paymentDate || EMPTY_VALUE}
          />
          <SummaryRow
            label={formatMessage({ id: 'payment.confirmPay.transferReason', defaultMessage: 'Transfer reason' })}
            value={summary.transferReason || EMPTY_VALUE}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="text" onClick={handleClose} disabled={isPaying}>
          {formatMessage({ id: 'button.cancel', defaultMessage: 'Cancel' })}
        </Button>
        <Button variant="contained" onClick={handleConfirm} loading={isPaying}>
          {formatMessage({ id: 'payment.confirmPay.confirm', defaultMessage: 'Confirm & Pay' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmPayDialogView;
